import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import {
  ChevronLeft, Plus, Trash2, Pencil, Link2 as LinkIcon, ExternalLink,
  Spline, X, User, MapPin, FileSearch, Lightbulb, HelpCircle, StickyNote, Loader2,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { photoUrl } from "@/pages/CaseWorkspace";
import { caseDisplayName } from "@/lib/caseName";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CARD_W = 224;
const CARD_H = 132;

const TYPES = {
  person: { label: "Person", icon: User, color: "sky" },
  place: { label: "Place", icon: MapPin, color: "emerald" },
  evidence: { label: "Evidence", icon: FileSearch, color: "amber" },
  theory: { label: "Theory", icon: Lightbulb, color: "violet" },
  question: { label: "Open Question", icon: HelpCircle, color: "rose" },
  note: { label: "Note", icon: StickyNote, color: "slate" },
};

const COLORS = {
  amber: { card: "bg-amber-500/15 border-amber-400/50", dot: "bg-amber-400", text: "text-amber-200" },
  sky: { card: "bg-sky-500/15 border-sky-400/50", dot: "bg-sky-400", text: "text-sky-200" },
  emerald: { card: "bg-emerald-500/15 border-emerald-400/50", dot: "bg-emerald-400", text: "text-emerald-200" },
  violet: { card: "bg-violet-500/15 border-violet-400/50", dot: "bg-violet-400", text: "text-violet-200" },
  rose: { card: "bg-rose-500/15 border-rose-400/50", dot: "bg-rose-400", text: "text-rose-200" },
  slate: { card: "bg-slate-400/10 border-slate-300/40", dot: "bg-slate-300", text: "text-slate-200" },
};

const emptyCard = { type: "note", title: "", body: "", color: "slate", link_type: null, link_id: null, link_label: null };

export default function EvidenceBoardPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [caseObj, setCaseObj] = useState(null);
  const [caseId, setCaseId] = useState(null);
  const [cards, setCards] = useState([]);
  const [connections, setConnections] = useState([]);
  const [docs, setDocs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // card being edited or null (new)
  const [form, setForm] = useState(emptyCard);

  const [connectMode, setConnectMode] = useState(false);
  const [connectFrom, setConnectFrom] = useState(null);
  const [pendingConn, setPendingConn] = useState(null); // {from, to}
  const [connLabel, setConnLabel] = useState("");

  const dragRef = useRef(null); // {cardId, dx, dy}
  const canvasRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: c } = await api.get(`/cases/${slug}`);
      setCaseObj(c);
      setCaseId(c.case_id);
      const [b, d, p] = await Promise.all([
        api.get(`/cases/${c.case_id}/board`),
        api.get(`/cases/${c.case_id}/documents`).catch(() => ({ data: [] })),
        api.get(`/cases/${c.case_id}/photos`).catch(() => ({ data: [] })),
      ]);
      setCards(b.data.cards || []);
      setConnections(b.data.connections || []);
      setDocs(d.data || []);
      setPhotos(p.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load board");
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // ---- Card editor ----
  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyCard });
    setEditorOpen(true);
  };
  const openEdit = (card) => {
    setEditing(card);
    setForm({ type: card.type, title: card.title, body: card.body, color: card.color,
      link_type: card.link_type, link_id: card.link_id, link_label: card.link_label });
    setEditorOpen(true);
  };
  const saveCard = async () => {
    if (!form.title.trim()) { toast.error("Give the card a title"); return; }
    try {
      if (editing) {
        const { data } = await api.put(`/cases/${caseId}/board/cards/${editing.card_id}`, form);
        setCards((cs) => cs.map((c) => (c.card_id === data.card_id ? data : c)));
      } else {
        // spread new cards in a grid so they don't stack
        const n = cards.length;
        const { data } = await api.post(`/cases/${caseId}/board/cards`, {
          ...form, x: 60 + (n % 4) * 260, y: 60 + (Math.floor(n / 4) % 3) * 170,
        });
        setCards((cs) => [...cs, data]);
      }
      setEditorOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const deleteCard = async (card) => {
    setCards((cs) => cs.filter((c) => c.card_id !== card.card_id));
    setConnections((xs) => xs.filter((x) => x.from_card_id !== card.card_id && x.to_card_id !== card.card_id));
    try { await api.delete(`/cases/${caseId}/board/cards/${card.card_id}`); }
    catch { toast.error("Delete failed"); load(); }
  };

  // ---- Link picker helpers ----
  const setLink = (value) => {
    if (value === "none") { setForm((f) => ({ ...f, link_type: null, link_id: null, link_label: null })); return; }
    const [lt, id] = value.split(":");
    if (lt === "document") {
      const d = docs.find((x) => x.document_id === id);
      setForm((f) => ({ ...f, link_type: "document", link_id: id, link_label: d?.title || "Document" }));
    } else {
      const p = photos.find((x) => x.photo_id === id);
      setForm((f) => ({ ...f, link_type: "photo", link_id: id, link_label: p?.caption || "Photo" }));
    }
  };
  const openLink = (card) => {
    if (card.link_type === "document") {
      const d = docs.find((x) => x.document_id === card.link_id);
      if (d?.storage_path) window.open(`${API}/files/${d.storage_path}`, "_blank");
      else if (d?.external_url) window.open(d.external_url, "_blank");
      else toast.error("Linked document not found");
    } else if (card.link_type === "photo") {
      const p = photos.find((x) => x.photo_id === card.link_id);
      const u = photoUrl(p);
      if (u) window.open(u, "_blank"); else toast.error("Linked photo not found");
    }
  };

  // ---- Dragging ----
  const onCardPointerDown = (e, card) => {
    if (connectMode) return;
    if (e.target.closest("[data-nodrag]")) return;
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = {
      cardId: card.card_id,
      dx: e.clientX - rect.left - card.x,
      dy: e.clientY - rect.top - card.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onCardPointerMove = (e) => {
    if (!dragRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.max(0, e.clientX - rect.left - dragRef.current.dx);
    const y = Math.max(0, e.clientY - rect.top - dragRef.current.dy);
    setCards((cs) => cs.map((c) => (c.card_id === dragRef.current.cardId ? { ...c, x, y } : c)));
  };
  const onCardPointerUp = async () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    const card = cards.find((c) => c.card_id === d.cardId);
    if (card) {
      try { await api.put(`/cases/${caseId}/board/cards/${card.card_id}`, { x: card.x, y: card.y }); }
      catch (e) { console.warn("Card position save failed", e); }
    }
  };

  // ---- Connections ----
  const onCardClick = (card) => {
    if (!connectMode) return;
    if (!connectFrom) { setConnectFrom(card.card_id); return; }
    if (connectFrom === card.card_id) { setConnectFrom(null); return; }
    setPendingConn({ from: connectFrom, to: card.card_id });
    setConnLabel("");
  };
  const confirmConnection = async () => {
    try {
      const { data } = await api.post(`/cases/${caseId}/board/connections`, {
        from_card_id: pendingConn.from, to_card_id: pendingConn.to, label: connLabel,
      });
      setConnections((xs) => [...xs, data]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not connect"); }
    setPendingConn(null); setConnectFrom(null); setConnectMode(false);
  };
  const deleteConnection = async (conn) => {
    setConnections((xs) => xs.filter((x) => x.conn_id !== conn.conn_id));
    try { await api.delete(`/cases/${caseId}/board/connections/${conn.conn_id}`); }
    catch { load(); }
  };

  const center = (id) => {
    const c = cards.find((x) => x.card_id === id);
    if (!c) return null;
    return { x: c.x + CARD_W / 2, y: c.y + CARD_H / 2 };
  };

  return (
    <div className="min-h-screen bg-charcoal flex flex-col">
      <Helmet><title>Evidence Board · Help Solve The Case</title></Helmet>
      <Navbar />

      {/* Sub-header / toolbar */}
      <div className="border-b border-white/10 bg-charcoal/95 sticky top-0 z-20">
        <div className="max-w-full px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <Link to={`/cases/${slug}`} data-testid="board-back-link" className="label-mono hover:text-gold transition-colors inline-flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" /> Back to case
            </Link>
            <h1 className="font-display text-xl font-bold text-white truncate">
              Evidence Board {caseObj && <span className="text-white/40 font-normal">· {caseDisplayName(caseObj.title)}</span>}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => { setConnectMode((m) => !m); setConnectFrom(null); }}
              data-testid="connect-mode-btn"
              variant="outline"
              className={`rounded-sm bg-transparent ${connectMode ? "border-rose-400 text-rose-300" : "border-white/15 text-white/80 hover:border-gold/40"}`}>
              <Spline className="w-4 h-4 mr-1.5" /> {connectMode ? "Cancel connect" : "Connect cards"}
            </Button>
            <Button onClick={openNew} data-testid="add-card-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm">
              <Plus className="w-4 h-4 mr-1.5" /> Add card
            </Button>
          </div>
        </div>
        {connectMode && (
          <div className="px-4 sm:px-6 pb-2 -mt-1">
            <p className="text-xs font-mono text-rose-300/90" data-testid="connect-hint">
              {connectFrom ? "Now click the second card to draw the string." : "Click the first card to start a connection."}
            </p>
          </div>
        )}
      </div>

      {/* Board */}
      <div className="flex-1 overflow-auto bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.06)_1px,transparent_0)] [background-size:22px_22px]">
        {loading ? (
          <div className="flex items-center justify-center h-[60vh] text-white/40 label-mono gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading board…</div>
        ) : (
          <div ref={canvasRef} className="relative" style={{ width: 2400, height: 1500 }}
            onPointerMove={onCardPointerMove} onPointerUp={onCardPointerUp} data-testid="board-canvas">
            {/* Red strings (visual only) */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: "visible" }}>
              {connections.map((conn) => {
                const a = center(conn.from_card_id); const b = center(conn.to_card_id);
                if (!a || !b) return null;
                return (
                  <g key={conn.conn_id}>
                    <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#e11d48" strokeWidth="2" opacity="0.85" />
                    <circle cx={a.x} cy={a.y} r="4" fill="#e11d48" />
                    <circle cx={b.x} cy={b.y} r="4" fill="#e11d48" />
                  </g>
                );
              })}
            </svg>

            {/* Connection labels + delete (real DOM so clicks are reliable) */}
            {connections.map((conn) => {
              const a = center(conn.from_card_id); const b = center(conn.to_card_id);
              if (!a || !b) return null;
              const mx = (a.x + b.x) / 2; const my = (a.y + b.y) / 2;
              return (
                <div key={conn.conn_id} className="absolute z-10 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1"
                  style={{ left: mx, top: my }} data-testid={`conn-${conn.conn_id}`}>
                  {conn.label ? <span className="text-[10px] text-rose-100 bg-rose-900/80 border border-rose-500/40 rounded px-1.5 py-0.5 max-w-[160px] truncate">{conn.label}</span> : null}
                  <button onClick={() => deleteConnection(conn)} data-testid={`delete-conn-${conn.conn_id}`} title="Remove connection"
                    className="w-5 h-5 rounded-full bg-rose-600 hover:bg-rose-500 text-white flex items-center justify-center shadow">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              );
            })}

            {/* Empty state */}
            {cards.length === 0 && (
              <div className="absolute left-1/2 top-40 -translate-x-1/2 text-center max-w-sm">
                <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center mx-auto mb-4"><StickyNote className="w-6 h-6 text-gold" /></div>
                <h3 className="font-display text-lg font-semibold text-white">Your board is empty</h3>
                <p className="text-sm text-white/50 mt-2">Pin your first card — a suspect, a location, a piece of evidence, or a theory — then connect the dots with red string.</p>
                <Button onClick={openNew} className="mt-5 bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1.5" /> Add your first card</Button>
              </div>
            )}

            {/* Cards */}
            {cards.map((card) => {
              const T = TYPES[card.type] || TYPES.note;
              const col = COLORS[card.color] || COLORS.slate;
              const Icon = T.icon;
              const selected = connectFrom === card.card_id;
              return (
                <div key={card.card_id}
                  data-testid={`board-card-${card.card_id}`}
                  onPointerDown={(e) => onCardPointerDown(e, card)}
                  onClick={() => onCardClick(card)}
                  className={`absolute rounded-md border shadow-lg backdrop-blur-sm select-none ${col.card} ${connectMode ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"} ${selected ? "ring-2 ring-rose-400" : ""}`}
                  style={{ left: card.x, top: card.y, width: CARD_W }}>
                  <div className="flex items-center justify-between px-3 pt-2.5">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider ${col.text}`}>
                      <Icon className="w-3.5 h-3.5" /> {T.label}
                    </span>
                    {!connectMode && (
                      <div className="flex items-center gap-1" data-nodrag>
                        <button onClick={() => openEdit(card)} data-testid={`edit-card-${card.card_id}`} className="text-white/40 hover:text-gold p-0.5"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteCard(card)} data-testid={`delete-card-${card.card_id}`} className="text-white/40 hover:text-red-400 p-0.5"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                  <div className="px-3 pb-3 pt-1.5">
                    <h4 className="font-semibold text-sm text-white leading-tight line-clamp-2">{card.title}</h4>
                    {card.body ? <p className="text-xs text-white/60 mt-1 leading-snug line-clamp-3 whitespace-pre-line">{card.body}</p> : null}
                    {card.link_type && (
                      <button onClick={() => openLink(card)} data-nodrag data-testid={`card-link-${card.card_id}`}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] text-gold hover:text-gold-hover max-w-full">
                        <LinkIcon className="w-3 h-3 shrink-0" /> <span className="truncate">{card.link_label || "Open link"}</span> <ExternalLink className="w-3 h-3 shrink-0" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Card editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-surface border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit card" : "New card"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-mono text-white/50 mb-1 block">Type</label>
                <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v, color: (TYPES[v]?.color) || f.color }))}>
                  <SelectTrigger className="bg-charcoal border-white/15" data-testid="card-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-mono text-white/50 mb-1 block">Color</label>
                <div className="flex items-center gap-2 h-10">
                  {Object.entries(COLORS).map(([k, v]) => (
                    <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, color: k }))}
                      data-testid={`card-color-${k}`}
                      className={`w-6 h-6 rounded-full ${v.dot} ${form.color === k ? "ring-2 ring-white" : "opacity-70"}`} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <label className="label-mono text-white/50 mb-1 block">Title</label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Suspect vehicle spotted" className="bg-charcoal border-white/15" data-testid="card-title-input" />
            </div>
            <div>
              <label className="label-mono text-white/50 mb-1 block">Notes</label>
              <Textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={3} placeholder="What does this mean? Why does it matter?" className="bg-charcoal border-white/15" data-testid="card-body-input" />
            </div>
            <div>
              <label className="label-mono text-white/50 mb-1 block">Link to case evidence (optional)</label>
              <Select value={form.link_id ? `${form.link_type}:${form.link_id}` : "none"} onValueChange={setLink}>
                <SelectTrigger className="bg-charcoal border-white/15" data-testid="card-link-select"><SelectValue placeholder="No link" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">No link</SelectItem>
                  {docs.map((d) => <SelectItem key={d.document_id} value={`document:${d.document_id}`}>📄 {d.title}</SelectItem>)}
                  {photos.map((p) => <SelectItem key={p.photo_id} value={`photo:${p.photo_id}`}>🖼 {p.caption || "Photo"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCard} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-card-btn">{editing ? "Save" : "Pin to board"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection label dialog */}
      <Dialog open={!!pendingConn} onOpenChange={(o) => { if (!o) { setPendingConn(null); setConnectFrom(null); } }}>
        <DialogContent className="bg-surface border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display">Connect the dots</DialogTitle></DialogHeader>
          <p className="text-sm text-white/60">What's the link between these two cards? (optional)</p>
          <Input value={connLabel} onChange={(e) => setConnLabel(e.target.value)} placeholder="e.g. alibi contradicts timeline" className="bg-charcoal border-white/15" data-testid="conn-label-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPendingConn(null); setConnectFrom(null); }} className="border-white/15 text-white/80 bg-transparent"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={confirmConnection} className="bg-rose-500 text-white hover:bg-rose-600" data-testid="confirm-conn-btn"><Spline className="w-4 h-4 mr-1.5" /> Draw string</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
